/**
 * Mediobanca brand style configuration — colors, typography, and prompt
 * rules extracted from the client's "Mediobanca Sample Deck (2).pptx"
 * (slide master colorScheme "Mediobanca" + fontScheme "Mediobanca",
 * layout placeholder geometry, and real template charts, via the OOXML).
 *
 * Mirrors the shape of `paretoStyleConfig.ts` / `berenbergStyleConfig.ts`.
 * When Mediobanca style is active the prompt below is appended to the
 * system prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: theme colors come from the master-linked colorScheme
 * "Mediobanca". The fontScheme names Baskerville Old Face (major) and
 * Century Gothic (minor); master titleStyle uses +mj-lt and bodyStyle
 * names Century Gothic explicitly, so THEME_COLORS follow those faces
 * rather than the Calibri that appears in leftover sample content. Slide
 * size is 780×540 pt (sldSz type A4). Title/content geometry is from the
 * "Title and Content" layout. A wordmark lives on slide master 1
 * (bottom-right), but that master's first layout is Cover Title, so
 * content slides must use contentLayoutName "Title and Content" rather
 * than the cover. Template charts lead with navy (theme accent1 / #003366).
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "Mediobanca" colorScheme
// ---------------------------------------------------------------------------

export const MEDIOBANCA_COLORS = {
  navy: "003366", // dk1 / accent1 — Mediobanca signature navy
  midBlue: "124A70", // accent2
  lightBlue: "81B0CD", // accent3
  charcoal: "333333", // accent4
  cream: "E8E4D1", // accent5
  gold: "BD9554", // accent6 / hlink

  white: "FFFFFF", // lt1
  silver: "BFBFBF", // dk2
  paleGray: "DFDFDF", // lt2
  followedHyperlink: "D9C3A6", // folHlink

  green: "00B050", // observed positive / up on sample slides
  red: "C00000", // observed negative / down on sample slides
  paleNavy: "E6EFF4", // observed table/shading tint

  ruleGray: "8F8F8F",
  altRow: "E6EFF4",
} as const;

/**
 * Drop-in ThemeColors override when Mediobanca style is active. Color
 * slots map 1:1 to the Mediobanca colorScheme. Fonts follow the
 * Mediobanca fontScheme (Baskerville Old Face titles, Century Gothic body).
 */
export const MEDIOBANCA_THEME_COLORS: ThemeColors = {
  dark1: MEDIOBANCA_COLORS.navy, // #003366 — Text1
  dark2: MEDIOBANCA_COLORS.silver, // #BFBFBF — Text2
  light1: MEDIOBANCA_COLORS.white, // #FFFFFF — Background1
  light2: MEDIOBANCA_COLORS.paleGray, // #DFDFDF — Background2
  accent1: MEDIOBANCA_COLORS.navy, // #003366
  accent2: MEDIOBANCA_COLORS.midBlue, // #124A70
  accent3: MEDIOBANCA_COLORS.lightBlue, // #81B0CD
  accent4: MEDIOBANCA_COLORS.charcoal, // #333333
  accent5: MEDIOBANCA_COLORS.cream, // #E8E4D1
  accent6: MEDIOBANCA_COLORS.gold, // #BD9554
  hyperlink: MEDIOBANCA_COLORS.gold,
  followedHyperlink: MEDIOBANCA_COLORS.followedHyperlink,
  majorFont: "Baskerville Old Face",
  minorFont: "Century Gothic",
};

/**
 * Ordered series palette. Template charts lead with navy (accent1 / dk1),
 * then mid blue, light blue, gold, charcoal, cream.
 */
export const MEDIOBANCA_CHART_SERIES_PALETTE = [
  MEDIOBANCA_COLORS.navy,
  MEDIOBANCA_COLORS.midBlue,
  MEDIOBANCA_COLORS.lightBlue,
  MEDIOBANCA_COLORS.gold,
  MEDIOBANCA_COLORS.charcoal,
  MEDIOBANCA_COLORS.cream,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from master + "Title and Content")
// ---------------------------------------------------------------------------

export const MEDIOBANCA_SLIDE_DIMENSIONS = { width: 780, height: 540 } as const;

export const MEDIOBANCA_POSITIONS = {
  title: { left: 35.8, top: 17.7, width: 708.7, height: 36.8 },
  contentStart: { left: 35.6, top: 79.9 },
  footnote: { left: 35.8, top: 54.5, width: 575.7, height: 25.4 },
} as const;

// Authoritative layout names from the Mediobanca master (trimmed catalog names).
export const MEDIOBANCA_LAYOUT_NAMES = [
  "Cover Title",
  "Title and Content",
  "Title and 2 Contents",
  "Title and 3 Contents (1)",
  "Title and 3 Contents (2)",
  "Title and 4 Contents",
  "Content and Caption",
  "2 Contents and Caption",
  "4 Contents and Caption",
  "Content with Coloured Caption",
  "Title Only",
  "DisclaimerSlide",
  "AgendaSlide",
  "Section Divider",
  "Subsection Divider",
  "1_Title Only",
  "1_Title and 2 Contents",
  "1_2 Contents and Caption",
  "Titolo e contenuto",
  "1_Title and 4 Contents",
  "Table Slide",
  "Title and Content Appendix",
  "Title and 2 Contents Appendix",
  "Title and 3 Contents (1) Appendix",
  "Title and 3 Contents (2) Appendix",
  "Title and 4 Contents (2) Appendix",
  "2 Contents and Caption Appendix",
  "4 Contents and Caption Appendix",
  "Appendix Content with Coloured Caption",
  "Title Only Appendix",
  "Appendix Section Divider",
  "Appendix Subsection Divider",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Mediobanca style is active
// ---------------------------------------------------------------------------

export const MEDIOBANCA_STYLE_PROMPT = `

## MEDIOBANCA BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Mediobanca.pptx" contains the current Mediobanca visual standard. Match the closest relevant precedent for covers, title-and-content pages, multi-column contents, tables, and appendix slides. Prefer its title/subtitle hierarchy, bottom-right wordmark, navy-led charts, and cream/gold accents over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Mediobanca uses:
- Titles / headings: \`"Baskerville Old Face"\`
- Body, bullets, tables, charts, notes, subtitles: \`"Century Gothic"\`
NEVER omit fontFamily when Mediobanca style is active. If a shape already has the wrong font (including Calibri from sample content), include fontFamily to correct it.

### Color palette (from the Mediobanca master colorScheme "Mediobanca")
Use ONLY these Mediobanca brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Navy #${MEDIOBANCA_COLORS.navy}
- Text2 / dark2: Silver #${MEDIOBANCA_COLORS.silver}
- Background1 / light1: White #${MEDIOBANCA_COLORS.white}
- Background2 / light2: Pale gray #${MEDIOBANCA_COLORS.paleGray}
- Accent1: Navy #${MEDIOBANCA_COLORS.navy}
- Accent2: Mid blue #${MEDIOBANCA_COLORS.midBlue}
- Accent3: Light blue #${MEDIOBANCA_COLORS.lightBlue}
- Accent4: Charcoal #${MEDIOBANCA_COLORS.charcoal}
- Accent5: Cream #${MEDIOBANCA_COLORS.cream}
- Accent6: Gold #${MEDIOBANCA_COLORS.gold}

**Usage:**
- **Primary brand / emphasis**: Navy #${MEDIOBANCA_COLORS.navy}
- **Secondary emphasis**: Mid blue #${MEDIOBANCA_COLORS.midBlue} or light blue #${MEDIOBANCA_COLORS.lightBlue}
- **Tertiary / highlight**: Gold #${MEDIOBANCA_COLORS.gold}
- **Text / ink**: Navy #${MEDIOBANCA_COLORS.navy} for titles; charcoal #${MEDIOBANCA_COLORS.charcoal} for body
- **Lines & borders**: Gray #${MEDIOBANCA_COLORS.ruleGray}
- **Background / shading**: Pale navy-tint #${MEDIOBANCA_COLORS.altRow} or cream #${MEDIOBANCA_COLORS.cream}
- **Up / down**: Green #${MEDIOBANCA_COLORS.green} / red #${MEDIOBANCA_COLORS.red}
- **Chart series palette** (in order): ${MEDIOBANCA_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${MEDIOBANCA_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from the Mediobanca master / "Title and Content" layout)
Slide dimensions: 780 × 540 pt (A4-labelled custom size).

**Placeholder positions:**
- **Title**: left=35.8, top=17.7, width=708.7, height=36.8 — "Baskerville Old Face" 22pt bold, navy (#${MEDIOBANCA_COLORS.navy}), left-aligned
- **Subtitle**: left=35.8, top=54.5, width=575.7, height=25.4 — "Century Gothic" 10pt
- **Content area**: starts at left=35.6, top=79.9, width=708.8, height=402.7
- **Wordmark**: bottom-right around left=608.4, top=486.6 (master picture — do not recreate)

**Content text hierarchy ("Century Gothic", charcoal #${MEDIOBANCA_COLORS.charcoal}, ~12pt body):**
- Keep body text ~10-12pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use "Baskerville Old Face" or the navy accent; bullets otherwise inherit charcoal text.

### Charts (Mediobanca style)
- Font: "Century Gothic", minimum 8pt
- **Primary data color**: navy (#${MEDIOBANCA_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Mediobanca style)
- Header row: navy (#${MEDIOBANCA_COLORS.navy}) fill with white text, bold
- Body: white fill, charcoal "Century Gothic" text (10pt)
- Alternate-row shading: pale navy-tint (#${MEDIOBANCA_COLORS.altRow})
- Borders: thin gray (#${MEDIOBANCA_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Mediobanca style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${MEDIOBANCA_COLORS.ruleGray}) or navy (#${MEDIOBANCA_COLORS.navy}) rule lines for separation when needed
- Connectors: solid gray (#${MEDIOBANCA_COLORS.ruleGray}) lines
- Page numbers / notes: 8-10pt, "Century Gothic"
- Prefer layout "Title and Content" for body slides. Use "Title and 2 Contents" / "Title and 3 Contents (1)" for multi-panel pages. Do not default to "Cover Title".
- Cover / section pages: "Cover Title", "Section Divider", "Subsection Divider". Appendix variants keep the "Appendix" suffix.

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
